import {
    TimeRange,
    PilotTrackingRangePicker,
    PilotTrackingActivityChart,
    RangePickerOption,
} from "@nova/component-charts"
import { createQuery } from "@nova/queryable"
import { useMemo } from "react"
import { usePilotTrackingQuery } from "../PilotTrackingQueries"
import { padTimeSeriesData } from "./pilotDataTransformation"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActivityTimeRange, setActivityTimeRange } from "./slice"

const rangeOptions: RangePickerOption<TimeRange>[] = [
    { title: "30D", value: "30d" },
    { title: "6M", value: "6m" },
    { title: "1Y", value: "1y" },
    { title: "All", value: "all" },
]

export const PilotActivityGraph = ({ userId }: { userId?: string }) => {
    const dispatch = useAppDispatch()
    const range = useAppSelector(selectActivityTimeRange)

    const { startDate, endDate, unit } = useMemo(() => {
        const end = new Date()
        end.setHours(23, 59, 59, 999)

        if (range === "all") {
            return { startDate: null as Date | null, endDate: end, unit: "month" as const }
        }

        const start = new Date()
        start.setHours(0, 0, 0, 0)

        let timeUnit: "day" | "week" | "month" = "day"

        if (range === "30d") {
            start.setDate(start.getDate() - 29)
            timeUnit = "day"
        } else if (range === "6m") {
            start.setMonth(start.getMonth() - 6)
            timeUnit = "week"
        } else if (range === "1y") {
            start.setFullYear(start.getFullYear() - 1)
            timeUnit = "month"
        }

        return { startDate: start as Date | null, endDate: end, unit: timeUnit }
    }, [range])

    const query = useMemo(
        () =>
            createQuery({
                resource: "metadata",
                timeBuckets: [{ field: "started_at", unit, alias: "bucket" }],
                aggregates: [{ func: "SUM", field: "duration_hours", alias: "hoursFlown" }],
                ...(userId
                    ? { filters: [{ field: "mission.user_id", op: "=", value: userId }] }
                    : {}),
            }),
        [userId, unit]
    )

    const { data: rawDbData, isLoading } = usePilotTrackingQuery(query)

    const chartData = useMemo(() => {
        const rawData = rawDbData ?? []
        if (startDate !== null) {
            return padTimeSeriesData(rawData, "bucket", "hoursFlown", startDate, endDate, unit)
        }
        if (!rawData.length) return []
        const bucketTimes = rawData.flatMap((row) => (row.bucket ? [row.bucket.getTime()] : []))
        if (!bucketTimes.length) return []
        const effectiveStart = new Date(Math.min(...bucketTimes))
        effectiveStart.setHours(0, 0, 0, 0)
        return padTimeSeriesData(rawData, "bucket", "hoursFlown", effectiveStart, endDate, unit)
    }, [rawDbData, startDate, endDate, unit])

    return (
        <div className="h-full flex flex-col">
            <div className="flex items-center justify-between">
                <h5 className="text-gray-400">Flight time in hours</h5>
                <PilotTrackingRangePicker
                    options={rangeOptions}
                    value={range}
                    onChange={(v) => dispatch(setActivityTimeRange(v.value))}
                />
            </div>

            <div className="flex-1 min-h-0 w-full rounded-md flex items-center justify-center">
                {isLoading ? (
                    <span className="text-gray-500 animate-pulse">Loading flights...</span>
                ) : (
                    <PilotTrackingActivityChart data={chartData} range={range} />
                )}
            </div>
        </div>
    )
}
